import React, { useState, useEffect, useRef } from "react";
import firebase from "firebase/compat/app";
import { db, auth } from "../../packages/excalidraw/firebase";
import "./GlobalComments.scss";

export const GlobalComments: React.FC = () => {
  const [drawingId, setDrawingId] = useState<string | null>((window as any).currentDrawingId || null);
  const [user, setUser] = useState<firebase.User | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [text, setText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(setUser);
    
    const handleDrawingId = (e: any) => {
      setDrawingId(e.detail);
    };
    
    window.addEventListener("drawingIdChanged", handleDrawingId);
    
    return () => {
      unsubscribeAuth();
      window.removeEventListener("drawingIdChanged", handleDrawingId);
    };
  }, []);

  useEffect(() => {
    if (!drawingId || !user) {
      setComments([]);
      return;
    }

    const unsubscribe = db.collection("comments")
      .where("drawingId", "==", drawingId)
      .orderBy("createdAt", "asc")
      .onSnapshot(
        (snapshot) => {
          setComments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        },
        (error) => {
          console.error("Comments fetch error:", error);
          // Fallback if index is missing
          db.collection("comments").where("drawingId", "==", drawingId).get().then(snap => {
            const fetched = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
              .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            setComments(fetched);
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
          }).catch(console.error);
        }
      );

    return () => unsubscribe();
  }, [drawingId, user]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !user || !drawingId) return;

    const payload = {
      drawingId,
      text: text.trim(),
      userId: user.uid,
      userName: user.displayName || "Anonymous",
      userPhoto: user.photoURL || "",
      createdAt: new Date().toISOString(),
    };

    setText("");

    try {
      await db.collection("comments").add(payload);
    } catch (err) {
      console.error("Failed to send comment", err);
    }
  };

  if (!user) {
    return (
      <div className="GlobalComments__empty">
        <p>Please login to Cloud to view and post comments.</p>
      </div>
    );
  }

  if (!drawingId) {
    return (
      <div className="GlobalComments__empty">
        <p>Please open or create a drawing from My Workspace to start commenting.</p>
      </div>
    );
  }

  return (
    <div className="GlobalComments">
      <div className="GlobalComments__list">
        {comments.length === 0 ? (
          <div className="GlobalComments__no-comments">No comments yet. Start the conversation!</div>
        ) : (
          comments.map((c) => (
            <div key={c.id} className={`GlobalComments__message ${c.userId === user.uid ? 'mine' : ''}`}>
              <img src={c.userPhoto || "https://via.placeholder.com/32"} alt="Avatar" className="GlobalComments__avatar" />
              <div className="GlobalComments__bubble">
                <div className="GlobalComments__author">{c.userName}</div>
                <div className="GlobalComments__text">{c.text}</div>
                <div className="GlobalComments__time">
                  {new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <form className="GlobalComments__form" onSubmit={handleSend}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a comment..."
          className="GlobalComments__input"
        />
        <button type="submit" className="GlobalComments__send" disabled={!text.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};
